import { createSlice } from '@reduxjs/toolkit';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { CellTowerCountryListResponseType } from '../../types/cell-towers/cell-towers.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import snackbarUtils from '../../utils/SnackbarUtils';
import { AsyncTaskType } from '../../types/async-task/async-task.type';
import {
  getCellTowersCountriesList,
  getOpenCellIdFilesSyncTask,
  postOpenCellIdFileSync,
  postOpenCellIdFilesSync,
} from './cellTowers.action';

export interface CellTowersState {
  countriesList: CellTowerCountryListResponseType | null;
  countriesListFetchStatus: FETCH_STATUS;
  openCellIdFilesSyncPostStatus: FETCH_STATUS;
  openCellIdFilesSyncTask: AsyncTaskType | null;
  openCellIdFileSyncStatus: FETCH_STATUS;
  cellTowersRouteParams: RouteParamsType;
}

export const initialCellTowersRouteParams: RouteParamsType = {
  page: 1,
  limit: 10,
  order: 'name',
  q: '',
};

const INIT_STATE: CellTowersState = {
  countriesList: null,
  countriesListFetchStatus: FETCH_STATUS.NULL,
  openCellIdFilesSyncPostStatus: FETCH_STATUS.NULL,
  openCellIdFilesSyncTask: null,
  openCellIdFileSyncStatus: FETCH_STATUS.NULL,
  cellTowersRouteParams: initialCellTowersRouteParams,
};

const cellTowersSlice = createSlice({
  name: 'cellTowers',
  initialState: INIT_STATE,
  reducers: {
    setCellTowersCountriesList: (state, action) => {
      state.countriesList = action.payload;
    },
    setCellTowersCountriesListFetchStatus: (state, action) => {
      state.countriesListFetchStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'cellTowers') {
        state.cellTowersRouteParams = { ...state.cellTowersRouteParams, ...payload.data };
      }
    });

    builder
      .addCase(getCellTowersCountriesList.pending, state => {
        state.countriesListFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getCellTowersCountriesList.rejected, (state, action) => {
        state.countriesListFetchStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error(action.payload);
      })
      .addCase(getCellTowersCountriesList.fulfilled, (state, action) => {
        state.countriesListFetchStatus = FETCH_STATUS.FULFILLED;
        state.countriesList = action.payload;
      })
      .addCase(postOpenCellIdFilesSync.pending, state => {
        state.openCellIdFilesSyncPostStatus = FETCH_STATUS.PENDING;
      })
      .addCase(postOpenCellIdFilesSync.rejected, (state, action) => {
        state.openCellIdFilesSyncPostStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error(action.payload);
      })
      .addCase(postOpenCellIdFilesSync.fulfilled, (state, action) => {
        state.openCellIdFilesSyncPostStatus = FETCH_STATUS.FULFILLED;
        state.openCellIdFilesSyncTask = action.payload;
        snackbarUtils.success('Cell tower file sync started successfully!');
      })
      .addCase(getOpenCellIdFilesSyncTask.rejected, (_, action) => {
        snackbarUtils.error(action.payload);
      })
      .addCase(getOpenCellIdFilesSyncTask.fulfilled, (state, action) => {
        state.openCellIdFilesSyncTask = action.payload;
      })
      .addCase(postOpenCellIdFileSync.pending, state => {
        state.openCellIdFileSyncStatus = FETCH_STATUS.PENDING;
      })
      .addCase(postOpenCellIdFileSync.rejected, (state, action) => {
        state.openCellIdFileSyncStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error(action.payload);
      })
      .addCase(postOpenCellIdFileSync.fulfilled, state => {
        state.openCellIdFileSyncStatus = FETCH_STATUS.FULFILLED;
        snackbarUtils.success('OpenCelliD file synced successfully.');
      });
  },
});

export const { setCellTowersCountriesList, setCellTowersCountriesListFetchStatus } = cellTowersSlice.actions;

export default cellTowersSlice.reducer;
